import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { System } from '../../shared-components/System';
import { Observable } from 'rxjs';
import { AdminAttendanceStudentPeriod, AttendanceMassEditFilter } from './attendance-mass-edit/attendance-mass-edit/attendance-mass-edit.model';
import { StudentAttendanceCodes } from './attendance-codes/attendance-codes.model';
import { YearService } from '../../shared-components/year/year.service';
import { AttendanceCheckInOut, CheckInOutSettings } from './attendance-check-in-out/attendance-check-in-out.model';
import { Attachment } from '../../shared-components/models/attachment.model';
import { AllStudentsAttendanceHistory, AttendanceByStudent } from './attendance-by-student/attendance-by-student.model';
import { CheckInOutSubmissionRequest } from '../../student360/student-summary/check-in-out-submission-request-modal/check-in-out-submission-request-modal.model';
import { AbsentNotice } from './review-absent-notices/absent-notice.model';
import { AttendanceGenerateLetter, AttendanceLetters, AttendanceStepHistoryDetails } from './attendance-letters/attendance-letters.model';
import { EFormTemplate } from '../../shared-components/e-forms/e-form-template.model';
import { AttendanceHistory, AttendanceHistoryBySchool, AttendanceHistoryPeriod } from './attendance-history/attendance-history-by-school.model';
import { SpecialAdditionalAttendanceFilter } from './special-additional-attendance/special-attendance-filter.model';
import { SpecialAdditionalAttendance, SpecialAttendanceByClass, SpecialAttendanceCourse, SpecialAttendanceExtraMeetings, SpecialAttendanceFilterOptions } from './special-additional-attendance/special-additional-attendance.model';

@Injectable({
  providedIn: 'root'
})
export class AttendanceAdministrationService {

  attendanceMassEditFilter: AttendanceMassEditFilter;
  specialAdditionalAttendanceFilter: SpecialAdditionalAttendanceFilter;

  constructor(
    private http: HttpClient,
    private yearService: YearService
  ) { }

  getAdminStudentAttendance(siteYearId: number, searchParams: AttendanceMassEditFilter): Observable<AdminAttendanceStudentPeriod[]> {
    const queryString = '?siteYearId=' + siteYearId;
    return this.http.post<AdminAttendanceStudentPeriod[]>(System.apiURL.Admin + 'GetAdminStudentAttendance' + queryString, searchParams);
  }

  getStudentAttendanceCodes(yearId: number, siteId: number): Observable<StudentAttendanceCodes[]> {
    const queryString = '?yearId=' + yearId +
      '&siteId=' + siteId;
    return this.http.get<StudentAttendanceCodes[]>(System.apiURL.Admin + 'GetStudentAttendanceCodes' + queryString);
  }

  getAttendanceCheckInOutByDate(attendanceDate: string): Observable<AttendanceCheckInOut[]> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id +
      '&siteId=' + this.yearService.defaultYear.SchoolID +
      '&date=' + attendanceDate;
    return this.http.get<AttendanceCheckInOut[]>(System.apiURL.Admin + 'GetAttendanceCheckInOutByDate' + queryString);
  }

  saveCheckInOutHistoryAttachment(historyId: number, attachment: Attachment[]): Observable<boolean> {
    const queryString = '?historyId=' + historyId;
    return this.http.post<boolean>(System.apiURL.Admin + 'SaveCheckInOutHistoryAttachment' + queryString, attachment);
  }

  deleteCheckInOutRecordById(recordId: number, type: string): Observable<boolean> {
    const queryString = '?recordId=' + recordId +
      '&type=' + type;
    return this.http.get<boolean>(System.apiURL.Admin + 'DeleteCheckInOutRecordById' + queryString);
  }

  getSchoolDaysBetweenDates(siteYearId: number, startDate: string, endDate: string, studentId: number = 0, actionDays: number = 0): Observable<Date[]> {
    const queryString = '?siteYearId=' + siteYearId +
      '&startDate=' + startDate +
      '&endDate=' + endDate +
      '&studentId=' + studentId +
      '&actionDays=' + actionDays;
    return this.http.get<Date[]>(System.apiURL.Admin + 'GetSchoolDaysBetweenDates' + queryString);
  }

  saveAdminStudentAttendance(siteYearId: number, attendance: AdminAttendanceStudentPeriod[]): Observable<boolean> {
    const queryString = '?siteYearId=' + siteYearId;
    return this.http.post<boolean>(System.apiURL.Admin + 'SaveAdminStudentAttendance' + queryString, attendance);
  }

  getAttendanceHistoryByStudentIdDate(studentId: number, attendanceDate: string, time: string, type: string, attendanceCodeId: number, dailyComment: string, overwritePriorPeriods: boolean, updatePeriodComment: boolean, periodCommentAppOROver: string, updateDailyComment: boolean, dailyCommentAppOROver: string, clearAtt = false, includeAttendanceHistoryLogs = false): Observable<AttendanceByStudent[]> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id +
      '&siteId=' + this.yearService.defaultYear.SchoolID +
      '&studentId=' + studentId +
      '&attendanceDate=' + attendanceDate +
      '&time=' + time +
      '&type=' + type +
      '&attendanceCodeId=' + attendanceCodeId +
      '&dailyComment=' + dailyComment +
      '&overwritePriorPeriods=' + overwritePriorPeriods +
      '&updatePeriodComment=' + updatePeriodComment +
      '&periodCommentAppOROver=' + periodCommentAppOROver +
      '&updateDailyComment=' + updateDailyComment +
      '&dailyCommentAppOROver=' + dailyCommentAppOROver +
      '&clearAtt=' + clearAtt +
      '&includeAttendanceHistoryLogs=' + includeAttendanceHistoryLogs;
    return this.http.get<AttendanceByStudent[]>(System.apiURL.Admin + 'GetAttendanceHistoryByStudentIdDate' + queryString);
  }

  saveStudentAttendanceHistory(studentAttendance: AllStudentsAttendanceHistory[]): Observable<string> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id +
      '&siteId=' + this.yearService.defaultYear.SchoolID;
    return this.http.post<string>(System.apiURL.Admin + 'SaveStudentAttendanceHistory' + queryString, studentAttendance);
  }

  saveAttendanceHistoryCheckInOutByStudentIdDate(studentId: number, attendanceDate: string, time: string, type: string, attendanceCodeId: number, dailyComment: string, missedMinutes: number, studentAttendance: AllStudentsAttendanceHistory, clearAtt = false): Observable<boolean> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id +
      '&siteId=' + this.yearService.defaultYear.SchoolID +
      '&studentId=' + studentId +
      '&attendanceDate=' + attendanceDate +
      '&time=' + time +
      '&type=' + type +
      '&attendanceCodeId=' + attendanceCodeId +
      '&dailyComment=' + dailyComment +
      '&missedMinutes=' + missedMinutes +
      '&clearAtt=' + clearAtt;
    return this.http.post<boolean>(System.apiURL.Admin + 'SaveAttendanceHistoryCheckInOutByStudentIdDate' + queryString, studentAttendance);
  }

  saveMassAttendanceHistoryCheckInOutByStudentIdDate(attendanceDate: string, checkInOutSettings: CheckInOutSettings[], clearAtt = false): Observable<boolean> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id +
      '&siteId=' + this.yearService.defaultYear.SchoolID +
      '&attendanceDate=' + attendanceDate +
      '&clearAtt=' + clearAtt;
    return this.http.post<boolean>(System.apiURL.Admin + 'SaveMassAttendanceHistoryCheckInOutByStudentIdDate' + queryString, checkInOutSettings);
  }

  getAttendanceSelfCheckInOutByStudentId(studentId: number): Observable<CheckInOutSubmissionRequest[]> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id +
      '&studentId=' + studentId;
    return this.http.get<CheckInOutSubmissionRequest[]>(System.apiURL.Admin + 'GetAttendanceCheckInOutSubmissionByStudentId' + queryString);
  }

  getCheckInOutHistoryAttachmentById(historyId: number, type: string): Observable<Attachment[]> {
    const queryString = '?historyId=' + historyId +
      '&type=' + type;
    return this.http.get<Attachment[]>(System.apiURL.Admin + 'GetCheckInOutHistoryAttachmentById' + queryString);
  }

  getAbsentNoticesByStudentIdAndYearId(studentId: number): Observable<AbsentNotice[]> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id +
      '&studentId=' + studentId;
    return this.http.get<AbsentNotice[]>(System.apiURL.Admin + 'GetAbsentNoticesByStudentIdAndYearId' + queryString);
  }

  getAbsentNoticeAttachmentById(id: number): Observable<Attachment[]> {
    const queryString = '?id=' + id;
    return this.http.get<Attachment[]>(System.apiURL.Admin + 'GetAbsentNoticeAttachmentById' + queryString);
  }

  processSubmissionRecord(historyId: number, submissionId: number): Observable<boolean> {
    const queryString = '?historyId=' + historyId +
      '&submissionId=' + submissionId;
    return this.http.get<boolean>(System.apiURL.Admin + 'ProcessSubmissionRecord' + queryString);
  }

  getAttendanceCheckInOutHistoryByStudentId(schoolId: number, studentId: number): Observable<AttendanceCheckInOut[]> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id +
      '&siteId=' + schoolId +
      '&studentId=' + studentId;
    return this.http.get<AttendanceCheckInOut[]>(System.apiURL.Admin + 'GetAttendanceCheckInOutHistoryByStudentId' + queryString);
  }

  getAllPeriodsByYear(yearId: number): Observable<string[]> {
    const queryString = '?yearId=' + yearId;
    return this.http.get<string[]>(System.apiURL.Admin + 'GetAllPeriodsByYear' + queryString);
  }

  getAttendanceStepRunHistoryBySiteYearId(): Observable<AttendanceLetters[]> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id +
      '&siteId=' + this.yearService.defaultYear.SchoolID;
    return this.http.get<AttendanceLetters[]>(System.apiURL.Admin + 'GetAttendanceStepRunHistoryBySiteYearId' + queryString);
  }

  runGroupStepRunUsingWorkFlow(startDate: string, endDate: string, attendanceStepDefinitionId: number, studentIds: string): Observable<string> {
    const queryString = '?siteYearId=' + this.yearService.defaultYear.SiteYearId +
      '&startDate=' + startDate +
      '&endDate=' + endDate +
      '&attendanceStepDefinitionId=' + attendanceStepDefinitionId +
      '&studentIds=' + studentIds;
    return this.http.get<string>(System.apiURL.Admin + 'RunGroupStepRunUsingWorkFlow' + queryString);
  }

  deleteAttendanceStepHistory(stepHistory: AttendanceLetters[]): Observable<boolean> {
    const queryString = '?siteYearId=' + this.yearService.defaultYear.SiteYearId;
    return this.http.post<boolean>(System.apiURL.Admin + 'DeleteAttendanceStepHistory' + queryString, stepHistory);
  }

  getAttendanceLetterTemplates(templateId: number = 0): Observable<EFormTemplate[]> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id +
      '&schoolId=' + this.yearService.defaultYear.SchoolID +
      '&templateId=' + templateId;
    return this.http.get<EFormTemplate[]>(System.apiURL.Admin + 'GetAttendanceLetterTemplates' + queryString);
  }

  saveAttendanceLetterTemplate(template: EFormTemplate): Observable<EFormTemplate> {
    return this.http.post<EFormTemplate>(System.apiURL.Admin + 'SaveAttendanceLetterTemplate', template);
  }

  getAttendanceStepHistoryDetails(trackingType: number, gradeId: number, attendanceStepHistoryId: number, batchRunDate: Date, stepSequence: number, attendanceStepDefId: number, filterByBatchDate: boolean): Observable<AttendanceStepHistoryDetails[]> {
    const queryString = '?siteYearId=' + this.yearService.defaultYear.SiteYearId +
      '&trackingType=' + trackingType +
      '&gradeId=' + gradeId +
      '&attendanceStepHistoryId=' + attendanceStepHistoryId +
      '&batchRunDate=' + batchRunDate +
      '&stepSequence=' + stepSequence +
      '&attendanceStepDefId=' + attendanceStepDefId +
      '&filterByBatchDate=' + filterByBatchDate;
    return this.http.get<AttendanceStepHistoryDetails[]>(System.apiURL.Admin + 'GetAttendanceStepHistoryDetails' + queryString);
  }

  getAttendanceStepHistoryDetailsByStudent(studentId: number): Observable<AttendanceStepHistoryDetails[]> {
    const queryString = '?siteYearId=' + this.yearService.defaultYear.SiteYearId +
      '&studentId=' + studentId;
    return this.http.get<AttendanceStepHistoryDetails[]>(System.apiURL.Admin + 'GetAttendanceStepHistoryDetailsByStudent' + queryString);
  }

  getAttendanceHistoryByStudentAndYear(studentId: number, yearId: number): Observable<AttendanceHistoryBySchool[]> {
    const queryString = '?yearId=' + yearId +
      '&studentId=' + studentId;
    return this.http.get<AttendanceHistoryBySchool[]>(System.apiURL.Admin + 'GetAttendanceHistoryByStudentAndYear' + queryString);
  }

  getAttendanceChangeHistoryByStudentAndDate(studentId: number, attendanceDate: Date): Observable<AttendanceHistory[]> {
    const queryString = '?studentId=' + studentId +
      '&attendanceDate=' + attendanceDate;
    return this.http.get<AttendanceHistory[]>(System.apiURL.Admin + 'GetAttendanceChangeHistoryByStudentAndDate' + queryString);
  }

  getAttendancePeriodsByStudentIdAndDailyCalendarId(studentId: number, dailyCalendarId: number): Observable<AttendanceHistoryPeriod[]> {
    const queryString = '?studentId=' + studentId +
      '&dailyCalendarId=' + dailyCalendarId;
    return this.http.get<AttendanceHistoryPeriod[]>(System.apiURL.Admin + 'GetAttendancePeriodsByStudentIdAndDailyCalendarId' + queryString);
  }

  updateAttendanceStepHistoryById(id: number, appealedStatus: number, approveByPeopleId: number, comment: string, aprrovedDate: string, reviewdDate: string): Observable<boolean> {
    const queryString = '?id=' + id +
      '&appealedStatus=' + appealedStatus +
      '&approveByPeopleId=' + approveByPeopleId +
      '&comment=' + comment +
      '&aprrovedDate=' + aprrovedDate +
      '&reviewdDate=' + reviewdDate;
    return this.http.get<boolean>(System.apiURL.Admin + 'UpdateAttendanceStepHistoryById' + queryString);
  }

  deleteAttendanceStepHistoryByIds(ids: number[], trackingType: number, batchRunDate: Date): Observable<boolean> {
    const queryString = '?siteYearId=' + this.yearService.defaultYear.SiteYearId +
      '&trackingType=' + trackingType +
      '&batchRunDate=' + batchRunDate;
    return this.http.post<boolean>(System.apiURL.Admin + 'DeleteAttendanceStepHistoryByIds' + queryString, ids);
  }

  deleteAttendanceHistoryByStudentIdAndDailyCalendarId(studentId: number, dailyCalendarId: number, siteYearId: number): Observable<boolean> {
    const queryString = '?studentId=' + studentId +
      '&dailyCalendarId=' + dailyCalendarId +
      '&siteYearId=' + siteYearId;
    return this.http.get<boolean>(System.apiURL.Admin + 'DeleteAttendanceHistoryByStudentIdAndDailyCalendarId' + queryString);
  }

  public saveAttendanceLetters(attendanceLetters: AttendanceGenerateLetter[]): Observable<any> {
    const queryString = '?siteYearId=' + this.yearService.defaultYear.SiteYearId;
    return this.http.post<any>(System.apiURL.Admin + 'SaveAttendanceLetters' + queryString, attendanceLetters);
  }

  getSpecialAttendanceByStudentsAndDates(siteYearId: number, startDate: string, endDate: string, lastName: string, firstName: string, studentNumber: string, codeId: number = 0): Observable<SpecialAdditionalAttendance[]> {
    const queryString = '?siteYearId=' + siteYearId
      + '&startDate=' + startDate
      + '&endDate=' + endDate
      + '&lastName=' + lastName
      + '&firstName=' + firstName
      + '&studentNumber=' + studentNumber
      + '&codeId=' + codeId;
    return this.http.get<SpecialAdditionalAttendance[]>(System.apiURL.Admin + 'GetSpecialAttendanceByStudentsAndDates' + queryString);
  }

  massUpdateSpecialAttendance(specialAttendance: SpecialAdditionalAttendance[], action: number): Observable<boolean> {
    const queryString = '?action=' + action;
    return this.http.post<boolean>(System.apiURL.Admin + 'MassUpdateSpecialAttendanceLogs' + queryString, specialAttendance);
  }

  deleteSpecialAttendance(specialAttendanceIds: number[]): Observable<boolean> {
    return this.http.post<boolean>(System.apiURL.Admin + 'DeleteSpecialAttendance', specialAttendanceIds);
  }

  getSpecialAttendanceBySiteSectionIdAndDate(siteSectionId: number, meetingDate: string): Observable<SpecialAdditionalAttendance[]> {
    const queryString = '?siteSectionId=' + siteSectionId
      + '&meetingDate=' + meetingDate;
    return this.http.get<SpecialAdditionalAttendance[]>(System.apiURL.Admin + 'GetSpecialAttendanceBySiteSectionIdAndDate' + queryString);
  }

  getAttendanceExtraMeetingReadBySiteSectionId(siteSectionId: number): Observable<SpecialAttendanceExtraMeetings[]> {
    const queryString = '?siteSectionId=' + siteSectionId;
    return this.http.get<SpecialAttendanceExtraMeetings[]>(System.apiURL.Admin + 'GetAttendanceExtraMeetingReadBySiteSectionId' + queryString);
  }

  getSpecialAttendanceClassesBySiteYearIdAndFilter(siteYearId: number, startDate: string, endDate: string, filter: SpecialAdditionalAttendanceFilter): Observable<SpecialAttendanceByClass[]> {
    const queryString = '?siteYearId=' + siteYearId
      + '&section=' + filter.section
      + '&code=' + filter.subjectCode
      + '&title=' + filter.courseName
      + '&startDate=' + startDate
      + '&endDate=' + endDate;
    return this.http.get<SpecialAttendanceByClass[]>(System.apiURL.Admin + 'GetSpecialAttendanceClassesBySiteYearIdAndFilter' + queryString);
  }

  getSpecialAttendanceFilterOptionsBySiteYearId(siteYearId: number): Observable<SpecialAttendanceFilterOptions> {
    const queryString = '?siteYearId=' + siteYearId;
    return this.http.get<SpecialAttendanceFilterOptions>(System.apiURL.Admin + 'GetSpecialAttendanceFilterOptionsBySiteYearId' + queryString);
  }

  getSpecialAttendanceCoursesBySiteYearId(siteYearId: number): Observable<SpecialAttendanceCourse[]> {
    const queryString = '?siteYearId=' + siteYearId;
    return this.http.get<SpecialAttendanceCourse[]>(System.apiURL.Admin + 'GetSpecialAttendanceCoursesBySiteYearId' + queryString);
  }

  getSpecialAttendanceCoursesByStudentIdAndSiteYearId(studentId: number, siteYearId: number): Observable<SpecialAttendanceCourse[]> {
    const queryString = '?studentId=' + studentId
      + '&siteYearId=' + siteYearId;
    return this.http.get<SpecialAttendanceCourse[]>(System.apiURL.Admin + 'GetSpecialAttendanceCoursesByStudentIdAndSiteYearId' + queryString);
  }

  addSpecialAttendanceExtraMeeting(extraMeeting: SpecialAttendanceExtraMeetings): Observable<boolean> {
    return this.http.post<boolean>(System.apiURL.Admin + 'AddSpecialAttendanceExtraMeeting', extraMeeting);
  }

  updateSpecialAttendanceExtraMeeting(extraMeeting: SpecialAttendanceExtraMeetings): Observable<boolean> {
    return this.http.post<boolean>(System.apiURL.Admin + 'UpdateSpecialAttendanceExtraMeeting', extraMeeting);
  }

  deleteSpecialAttendanceExtraMeeting(extraMeetingId: number): Observable<boolean> {
    const queryString = '?extraMeetingId=' + extraMeetingId;
    return this.http.get<boolean>(System.apiURL.Admin + 'DeleteSpecialAttendanceExtraMeeting' + queryString);
  }
}